import { useEffect, useState, useRef } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PaginatedListTable from '../../components/PaginatedListTable';
import EditStoreModal from '../../components/EditStoreModal';
import StoreImporterModal from '../../components/StoreImporterModal';
import FieldCell from '../../components/FieldCell';
import Container from '../../layouts/Container';
import { getRegionsWithProvinces } from '../../services/project/regionService';
import { getStores, deleteStore } from '../../services/project/storeService';
import { useUsers } from '../../context/UserContext';
import { Pencil, XOctagon  } from 'react-bootstrap-icons';
import AnchorTag from '../../components/AnchorTag';
import { useProjects } from '../../context/ProjectContext';
import { downloadStores } from '../../services/project/reportService';

function Stores() {
  const [selectedStore, setSelectedStore] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [regions, setRegions] = useState(null);
  const storeRef = useRef();
  const { hasPermission } = useUsers();
  const [isDownloading, setIsDownloading] = useState(false);
  const { projects, isProjectReadOnly } = useProjects();

  const { projectId, regionId } = useParams();

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const { regions } = await getRegionsWithProvinces(projectId);
        setRegions(regions);
      } catch(error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
    if (!regions) {
      fetchRegions();
    }
  }, []);


  const handleEditStore = (store) => {
    setSelectedStore(store);
    setShowModal(true);
  };

  const handleDeleteStore = async (projectId, storeCode) => {
    const confirmed = window.confirm('Are you sure?');
    if (!confirmed) return;
    try {
      const response = await deleteStore(projectId, storeCode);
    
      if (!response.success) {
        toast.error(response.data.error);
        return;
      }
      storeRef.current?.refetch();
      toast.success(response.message);
    } catch (error) {
      console.error('Error deleting store:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  const columnHelper = createColumnHelper();

  const canUpdateStore = hasPermission('Stores', 'Update');
  const canDeleteStore = hasPermission('Stores', 'Delete');

  const columns = [
    columnHelper.accessor('id', {
      header: 'ID',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('fields', {
      header: 'Fields',
      cell: info => <FieldCell value={info.getValue()} />,
    }),
    (!isProjectReadOnly && (canUpdateStore || canDeleteStore)) &&
      columnHelper.display({
        id: 'actions',
        header: 'Action',
        meta: { sticky: true },
        cell: info => {
          const store = info.row.original;
          return (
            <div className="d-flex gap-2">
              {canUpdateStore && (
                <AnchorTag onClick={() => handleEditStore(store)} hoverText='Edit'>
                  <Pencil />
                </AnchorTag>
              )}
              {canDeleteStore && (
                <AnchorTag onClick={() => handleDeleteStore(projectId, store.id)} hoverText='Delete'>
                  <XOctagon />
                </AnchorTag>
              )}
              
            </div>
          );
        },
      }),
  ].filter(Boolean);

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const project = projects.find(p => p.id === parseInt(projectId, 10));
      const firstWordLower = (project?.name || 'report').split(' ')[0].toLowerCase();
      await downloadStores(
        {
          projectId,
        },
        `${firstWordLower}_${new Date().toLocaleString()}.csv`
      );
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Container>
      <div className="d-flex align-items-center mb-3 gap-2">
        <h2>Store Details</h2>
        {!isProjectReadOnly && <StoreImporterModal projectId={projectId}/>}
        <Button variant="success" disabled={isDownloading} onClick={handleDownload}>
          {isDownloading ? <Spinner size="sm" /> : 'Export'}
        </Button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <PaginatedListTable
            ref={storeRef}
            columns={columns}
            rowIdAccessor={row => row.code}
            onFetchData={params => getStores(projectId, params)}
          />
          { (showModal) && (
            <EditStoreModal
              projectId={projectId}
              regionId={regionId}
              store={selectedStore}
              show={showModal}
              refetch={storeRef.current?.refetch}
              regions={regions}
              onClose={() => setShowModal(false)}
            />
          )}

        </>

      )}
    </Container>
  );
}

export default Stores;
